/* eslint-disable no-restricted-properties */
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math-v6';
import remarkParse from 'remark-parse';
import remarkStringify from 'remark-stringify';
import remarkStrip from 'strip-markdown';
import { unified } from 'unified';
import { visit } from 'unist-util-visit';

function stripLatex(latex: string): string {
  return (
    latex
      // Remove LaTeX commands (e.g., \command)
      .replace(/\\[a-zA-Z]+/g, '')
      // Remove curly braces (e.g., {content})
      .replace(/[{}]/g, ' ')
      // Remove other special LaTeX characters
      .replace(/[%&_]/g, '')
      // Replace multiple spaces with a single space
      .replace(/\s+/g, ' ')
      .trim()
  );
}

/**
 * Returns with the text without markdown notations and LaTeX commands.
 */
export async function removeMarkdown(text: string): Promise<string> {
  const preprocessedText = text
    // Convert \(...\) and \\(...\\) to $...$
    .replace(/\\?\\\(([\s\S]*?)\\?\\\)/g, '$$$1$$')
    // Convert \\[...\\] to $$...$$
    .replace(/\\\\(\[)([\s\S]*?)\\\\(\])/g, (_, _open, content) => `$$${content}$$`)
    // Convert \[...\] to $$...$$
    .replace(/\\\[([\s\S]*?)\\\]/g, (_, content) => `$$${content}$$`);

  const result = await unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkMath)
    .use(() => tree => {
      // Handle tables
      visit(tree, 'table', (node: any) => {
        const textContent: string[] = [];
        visit(node, 'text', (textNode: any) => {
          textContent.push(textNode.value);
        });
        node.type = 'paragraph';
        node.children = [
          {
            type: 'text',
            value: textContent.join(' '),
          },
        ];
      });

      // Handle code blocks
      visit(tree, 'code', (node: any) => {
        node.type = 'paragraph';
        node.children = [
          {
            type: 'text',
            value: node.value,
          },
        ];
      });

      // Handle inline math
      visit(tree, 'inlineMath', (node: any) => {
        node.type = 'text';
        node.value = stripLatex(node.value);
      });

      // Handle block math
      visit(tree, 'math', (node: any) => {
        node.type = 'paragraph';
        node.children = [
          {
            type: 'text',
            value: stripLatex(node.value),
          },
        ];
      });

      // Handle images - remove alt text and any trailing whitespace
      visit(tree, 'image', (node: any) => {
        node.type = 'text';
        node.value = '';
      });
    })
    .use(remarkStrip)
    .use(() => tree => {
      visit(tree, 'text', (node: any) => {
        // Normalize whitespace in text nodes
        node.value = node.value.trim();
      });
    })
    .use(remarkStringify)
    .process(preprocessedText);

  return result.toString();
}

export function countWords(text: string, locale = 'en'): number {
  const segmenter = new Intl.Segmenter(locale, { granularity: 'word' });
  const segments = segmenter.segment(text);

  let wordCount = 0;
  let previousSegment = null;

  for (const segment of segments) {
    // Check if the segment is a word
    if (segment.isWordLike) {
      // If the previous segment is a hyphen, consider it part of the current word
      if (previousSegment && previousSegment.segment === '-') {
        continue; // Skip counting the current segment separately
      }
      wordCount++;
    }
    previousSegment = segment;
  }

  return wordCount;
}
